import React from "react";
import {
  alpha,
  Avatar,
  Box,
  Chip,
  Container,
  Divider,
  Fade,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { 
  AutoGraph,
  Category,
  CheckCircleOutline,
  InsightsOutlined,
  RocketLaunchOutlined,
  SecurityOutlined,
} from '@mui/icons-material';
import BrandIcon from '../components/common/BrandIcon';
import { PageContainer } from '../components/common/StyledComponents';
import SurfaceCard from '../components/common/ui/SurfaceCard';

const AboutPage: React.FC = () => {
  const theme = useTheme();
  const accentSoft = alpha(theme.palette.primary.main, 0.22);
  const accentSofter = alpha(theme.palette.primary.main, 0.14);

  const coreCapabilities = [
    {
      icon: <AutoGraph />,
      title: "Live money visibility",
      description: "Track income and expenses in one place with monthly clarity at a glance.",
      color: theme.palette.primary.main,
    },
    {
      icon: <Category />,
      title: "Flexible categories",
      description: "Organize your finances your way with categories that match your real life.",
      color: theme.palette.success.main,
    },
    {
      icon: <InsightsOutlined />,
      title: "Actionable insights", 
      description: "Surface trends quickly so you can adjust spending before month-end surprises.",
      color: theme.palette.warning.main,
    },
  ];

  const productPrinciples = [
    {
      title: "Designed for momentum",
      description: "Fast entry flows and focused dashboards reduce friction every day.",
      icon: <RocketLaunchOutlined />,
    },
    {
      title: "Built with trust",
      description: "Clear states, predictable actions, and thoughtful defaults keep users in control.",
      icon: <SecurityOutlined />,
    },
    {
      title: "Clarity over clutter",
      description: "Every screen prioritizes what matters now so decisions are easier.",
      icon: <CheckCircleOutline />,
    },
  ];

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <Box
          sx={{
            py: { xs: 3, md: 5 },
            background: `radial-gradient(circle at 16% 8%, ${alpha(theme.palette.primary.main, 0.13)} 0%, transparent 34%)`,
          }}
        >
          <Fade in timeout={650}>
            <SurfaceCard
              sx={{
                mb: { xs: 2.5, md: 3.2 },
                p: { xs: 2.2, sm: 2.8, md: 3.4 },
                borderRadius: 3,
                border: `1px solid ${accentSoft}`,
                background: `linear-gradient(155deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
                position: "relative",
                overflow: "hidden",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: "auto -24% -70% auto",
                  width: 380,
                  height: 380,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${accentSoft} 0%, transparent 64%)`,
                },
              }}
            >
              <Grid container spacing={{ xs: 2.2, md: 3.2 }} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
                <Grid item xs={12} md={7}>
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.2 }}>
                    <Avatar sx={{ width: 42, height: 42, bgcolor: accentSoft, color: theme.palette.primary.light }}>
                      <BrandIcon size={24} />
                    </Avatar>
                    <Chip
                      label="About PocketBalance"
                      sx={{
                        borderRadius: "10px",
                        fontWeight: 700,
                        color: theme.palette.primary.light,
                        backgroundColor: accentSofter,
                      }}
                    />
                  </Stack>

                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      mb: 1,
                    }}
                  >
                    Financial clarity without the noise
                  </Typography>

                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 680 }}>
                    PocketBalance helps people understand where money goes, what to change, and what to do next. The product combines fast daily tracking with decision-ready monthly insights.
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ mt: 2.2, flexWrap: "wrap", gap: 1 }}>
                    <Chip label="Focused on clarity" sx={{ bgcolor: accentSofter, color: theme.palette.primary.light }} />
                    <Chip label="Built for daily use" sx={{ bgcolor: alpha(theme.palette.success.main, 0.16), color: theme.palette.success.light }} />
                  </Stack>
                </Grid>

                <Grid item xs={12} md={5}>
                  <SurfaceCard
                    sx={{
                      p: { xs: 1.8, md: 2.1 },
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                      background: alpha(theme.palette.background.paper, 0.62),
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Product direction
                    </Typography>
                    <Stack spacing={0.95}>
                      {productPrinciples.map((principle) => (
                        <Box
                          key={principle.title}
                          sx={{
                            p: 1.1,
                            borderRadius: 1.6,
                            backgroundColor: alpha(theme.palette.background.default, 0.6),
                            border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: accentSofter,
                                color: theme.palette.primary.light,
                              }}
                            >
                              {principle.icon}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {principle.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {principle.description}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </SurfaceCard>
                </Grid>
              </Grid>
            </SurfaceCard>
          </Fade>

          <Grid container spacing={2.2} sx={{ mb: { xs: 2.3, md: 3 } }}>
            {coreCapabilities.map((feature) => (
              <Grid item xs={12} md={4} key={feature.title}>
                <Fade in timeout={720}>
                  <SurfaceCard
                    sx={{
                      p: { xs: 2, md: 2.4 },
                      height: "100%",
                      borderRadius: 2,
                      border: `1px solid ${alpha(feature.color, 0.36)}`,
                      background: alpha(feature.color, 0.1),
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 12px 30px ${alpha(feature.color, 0.22)}`,
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 38,
                        height: 38,
                        mb: 1.4,
                        bgcolor: alpha(feature.color, 0.2),
                        color: feature.color,
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.65 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </SurfaceCard>
                </Fade>
              </Grid>
            ))}
          </Grid>

          <SurfaceCard
            sx={{
              p: { xs: 2.2, md: 2.8 },
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              background: alpha(theme.palette.background.paper, 0.72),
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.8 }}>
              Why this matters
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Budgeting tools often stop at recording transactions. PocketBalance is built to close the loop between tracking and decisions, so users can build better financial habits with less effort.
            </Typography>
            <Divider sx={{ my: 1.8, borderColor: alpha(theme.palette.text.primary, 0.08) }} />
            <Typography variant="body2" color="text.secondary">
              We focus on practical UX: fewer clicks, clearer language, and interfaces that feel calm even when finances are complex.
            </Typography>
          </SurfaceCard>
        </Box>
      </Container>
    </PageContainer>
  );
};

export default AboutPage;
